// ActionRail - Compact hover action bar for chat messages
// Appears on message hover with Copy, Regenerate, Sources, Rate, Flag actions

import { useState } from 'react';
import { Copy, RefreshCw, FileText, ThumbsUp, ThumbsDown, Flag, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface ActionRailProps {
  /** Current user rating (1 = helpful, 0 = not helpful, null = unrated) */
  userRating: number | null;
  /** Whether the message is flagged */
  isFlagged: boolean;
  /** Whether sources are available */
  hasSources: boolean;
  /** Whether regenerate is allowed (disabled for Wisr agent) */
  allowRegenerate: boolean;
  /** Callback when copy is clicked */
  onCopy: () => void;
  /** Callback when regenerate is clicked */
  onRegenerate: () => void;
  /** Callback when sources is clicked */
  onViewSources: () => void;
  /** Callback when rate is clicked (1 = helpful, 0 = not helpful) */
  onRate: (rating: number) => void;
  /** Callback when flag is clicked */
  onFlag: () => void;
  /** Additional CSS classes */
  className?: string;
}

export function ActionRail({
  userRating,
  isFlagged,
  hasSources,
  allowRegenerate,
  onCopy,
  onRegenerate,
  onViewSources,
  onRate,
  onFlag,
  className
}: ActionRailProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    onCopy();
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className={cn(
        'inline-flex items-center gap-0.5 p-1 rounded-lg bg-background/95 backdrop-blur-sm border border-border/50 shadow-lg',
        className
      )}
    >
      {/* Copy */}
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={handleCopy}
            >
              {copied ? (
                <Check className="h-3.5 w-3.5 text-green-600" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">
            {copied ? 'Copied!' : 'Copy'}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Regenerate */}
      {allowRegenerate && (
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={onRegenerate}
              >
                <RefreshCw className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">
              Regenerate
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {/* View Sources */}
      {hasSources && (
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={onViewSources}
              >
                <FileText className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">
              View Sources
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {/* Divider */}
      <div className="w-px h-4 bg-border mx-0.5" />

      {/* Rate Helpful */}
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                'h-7 w-7',
                userRating === 1 && 'text-green-600 bg-green-600/10'
              )}
              onClick={() => onRate(1)}
            >
              <ThumbsUp className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">
            Helpful
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Rate Not Helpful */}
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                'h-7 w-7',
                userRating === 0 && 'text-red-600 bg-red-600/10'
              )}
              onClick={() => onRate(0)}
            >
              <ThumbsDown className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">
            Not helpful
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Flag */}
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                'h-7 w-7',
                isFlagged && 'text-orange-600 bg-orange-600/10'
              )}
              onClick={onFlag}
            >
              <Flag className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">
            {isFlagged ? 'Flagged' : 'Report issue'}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}

export default ActionRail;
